import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, FormHelperText, MenuItem, Select } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'
import { formatNumber } from '@/common/helpers/formatUtilities'
import { getThicknessDisplay, PROFILE_TYPES } from '@/common/utils/SheetUtils/SheetUtils'

const classes = {
    formControl_thickness: {
        flex: 1,
        minWidth: 'fit-content',
    },
    formControl_sheet: {
        flex: 2,
        minWidth: 'fit-content',
    },
}

/**
 * Combined thickness and sheet size selectors for quote items.
 * Displays paired dropdowns for selecting material thickness and sheet size.
 * Sheet selection auto-populates profile, dimensions, and consumption mode.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.thicknessValue - The currently selected thickness option's composite value
 *   (see getThicknessOptionValue), e.g. '12' or, for an IBeam record, '8|12' (web|flange)
 * @param {string} props.sheetValue - The currently selected sheet ID
 * @param {Array<Object>} props.thicknessOptions - Available thickness options (QM-3717: option
 *   objects — profile, thickness, webThickness, value — not plain numbers, so an IBeam record
 *   can carry its distinguishing web thickness and render as "web/flange")
 * @param {Array<Object>} props.sheetOptions - Available sheet options
 * @param {string} props.locale - The locale for number formatting
 * @param {boolean} props.useImperialUnits - Whether to use imperial units
 * @param {string} props.units - The unit of measurement (mm or in)
 * @param {boolean} props.isRotaryPart - Whether the part is a rotary part
 * @param {boolean} [props.thicknessDisabled=false] - Whether the thickness selector is disabled
 * @param {boolean} [props.sheetDisabled=false] - Whether the sheet selector is disabled
 * @param {boolean} [props.isPartFromLibrary=false] - Whether the part is from the part library
 * @param {boolean} [props.isMaterialUnavailable=false] - Whether the material is unavailable for library parts
 * @param {Function} props.onThicknessChange - Callback when thickness selection changes
 * @param {Function} props.onSheetChange - Callback when sheet selection changes
 * @returns {React.ReactElement} A row with thickness and sheet dropdowns
 */
const ThicknessAndSheetControls = ({
    isMaterialUnavailable = false,
    isPartFromLibrary = false,
    isRotaryPart,
    locale,
    onSheetChange,
    onThicknessChange,
    sheetDisabled = false,
    sheetOptions,
    sheetValue,
    thicknessDisabled = false,
    thicknessOptions,
    thicknessValue,
    units,
    useImperialUnits,
}) => {
    const { t } = useTranslation()

    const getTooltipTitle = () => {
        if (!isPartFromLibrary) return ''
        if (isMaterialUnavailable) {
            return t('Material for Part Library Part is no longer available for selection')
        }
        return t('Cannot edit parts from library')
    }

    const tooltipTitle = getTooltipTitle()

    // QM-3717: a webbed-thickness hint, shown only when the visible options actually use the
    // web/flange format (getThicknessDisplay), so it doesn't appear for non-IBeam profiles.
    const hasIBeamOption = thicknessOptions?.some((option) => option.profile === PROFILE_TYPES.I_BEAM)

    return (
        <Box
            sx={{
                alignItems: 'start',
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                justifyContent: 'flex-end',
                width: '100%',
            }}
        >
            <TbxTooltip
                title={tooltipTitle}
                arrow
            >
                <FormControl
                    color="primary"
                    disabled={!thicknessOptions?.length}
                    margin="none"
                    size="small"
                    sx={classes.formControl_thickness}
                    variant="outlined"
                >
                    <Select
                        disabled={thicknessDisabled}
                        id="select-itemThickness"
                        inputProps={{ 'data-testid': 'nqp-quote-item-thickness' }}
                        labelId="select-itemThickness-label"
                        SelectDisplayProps={{ style: { textAlign: 'right' } }}
                        value={thicknessValue}
                        onChange={(e) => onThicknessChange(e)}
                    >
                        {thicknessOptions?.map((option) => (
                            <MenuItem
                                key={option.value}
                                sx={{ justifyContent: 'flex-end' }}
                                value={option.value}
                            >
                                {getThicknessDisplay(option, (value) => formatNumber(value, locale, useImperialUnits))}{' '}
                                {units}
                            </MenuItem>
                        ))}
                    </Select>
                    {hasIBeamOption ? <FormHelperText>{t('web/flange thickness')}</FormHelperText> : null}
                </FormControl>
            </TbxTooltip>
            <FormControl
                color="primary"
                disabled={!sheetOptions?.length}
                margin="none"
                size="small"
                sx={classes.formControl_sheet}
                variant="outlined"
            >
                <Select
                    disabled={sheetDisabled}
                    id="select-itemSheet"
                    inputProps={{ 'data-testid': 'nqp-quote-item-sheet-size' }}
                    labelId="select-itemSheet-label"
                    SelectDisplayProps={{ style: { textAlign: 'right' } }}
                    value={sheetValue}
                    onChange={(e) => onSheetChange(e)}
                >
                    {sheetOptions?.map((sheet) => (
                        <MenuItem
                            key={sheet.sheetId}
                            sx={{ justifyContent: 'flex-end' }}
                            value={sheet.sheetId}
                        >
                            {isRotaryPart
                                ? `${formatNumber(sheet.materialLength, locale)} ${units}`
                                : `${formatNumber(sheet.sheetWidth, locale)} x ${formatNumber(sheet.sheetHeight, locale)} ${units}`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

ThicknessAndSheetControls.propTypes = {
    isRotaryPart: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    sheetValue: PropTypes.string.isRequired,
    thicknessValue: PropTypes.string.isRequired,
    units: PropTypes.string.isRequired,
    useImperialUnits: PropTypes.bool.isRequired,
    onSheetChange: PropTypes.func.isRequired,
    onThicknessChange: PropTypes.func.isRequired,
    isMaterialUnavailable: PropTypes.bool,
    isPartFromLibrary: PropTypes.bool,
    sheetDisabled: PropTypes.bool,
    sheetOptions: PropTypes.arrayOf(
        PropTypes.shape({
            sheetId: PropTypes.string.isRequired,
            materialLength: PropTypes.number,
            sheetHeight: PropTypes.number,
            sheetWidth: PropTypes.number,
        })
    ),
    thicknessDisabled: PropTypes.bool,
    thicknessOptions: PropTypes.arrayOf(
        PropTypes.shape({
            thickness: PropTypes.number.isRequired,
            value: PropTypes.string.isRequired,
            profile: PropTypes.string,
            webThickness: PropTypes.number,
        })
    ),
}

export default memo(ThicknessAndSheetControls)
